import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { 
  Car, LayoutDashboard, FileText, LogOut, Users, Building, Monitor, Store, 
  BarChart3, DollarSign, Briefcase, TrendingUp, TrendingDown, Activity,
  PieChart, ArrowUpRight, ArrowDownRight
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { ParkWiseLogo } from "../../components/ui/parkwise-logo";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminRevenue() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [overview, setOverview] = useState(null);
  const [bySite, setBySite] = useState([]);
  const [byAdvertiser, setByAdvertiser] = useState([]);
  const [byScreen, setByScreen] = useState([]);
  const [byCampaignType, setByCampaignType] = useState({});
  const [kpis, setKpis] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [boosts, setBoosts] = useState([]);
  const [trend, setTrend] = useState(null);
  const [period, setPeriod] = useState("30d");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (user) {
      fetchAllData();
    }
  }, [user, period]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch {
      navigate("/admin/login");
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [overviewRes, siteRes, advRes, screenRes, typeRes, kpiRes, campRes, boostRes, trendRes] = await Promise.all([
        axios.get(`${API}/revenue/overview`, { withCredentials: true }),
        axios.get(`${API}/revenue/by-site`, { withCredentials: true }),
        axios.get(`${API}/revenue/by-advertiser`, { withCredentials: true }),
        axios.get(`${API}/revenue/by-screen`, { withCredentials: true }),
        axios.get(`${API}/revenue/by-campaign-type`, { withCredentials: true }),
        axios.get(`${API}/revenue/kpis`, { withCredentials: true }),
        axios.get(`${API}/revenue/campaigns/performance?limit=10`, { withCredentials: true }),
        axios.get(`${API}/revenue/boosts/performance?limit=10`, { withCredentials: true }),
        axios.get(`${API}/revenue/trend?period=${period}`, { withCredentials: true })
      ]);
      setOverview(overviewRes.data);
      setBySite(siteRes.data);
      setByAdvertiser(advRes.data);
      setByScreen(screenRes.data);
      setByCampaignType(typeRes.data);
      setKpis(kpiRes.data);
      setCampaigns(campRes.data);
      setBoosts(boostRes.data);
      setTrend(trendRes.data);
    } catch (error) {
      console.error("Failed to fetch revenue data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    navigate("/admin/login");
  };

  const formatCurrency = (amount) => `£${(amount || 0).toFixed(2)}`;

  return (
    <div className="admin-container min-h-screen" data-testid="admin-revenue">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-slate-900 border-r border-slate-800 p-6 overflow-y-auto">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-blue-600 to-green-500 flex items-center justify-center">
            <Car className="w-5 h-5 text-white" />
          </div>
          <span className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>ParkWise</span>
        </div>

        <nav className="space-y-2">
          <Link to="/admin/dashboard" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <LayoutDashboard className="w-5 h-5" /><span>Dashboard</span>
          </Link>
          <Link to="/admin/registrations" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <FileText className="w-5 h-5" /><span>Registrations</span>
          </Link>
          <Link to="/admin/sites" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <Building className="w-5 h-5" /><span>Sites & Kiosks</span>
          </Link>
          <Link to="/admin/retailers" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <Store className="w-5 h-5" /><span>Retailers</span>
          </Link>
          
          {/* Revenue Section */}
          <div className="pt-4 mt-4 border-t border-slate-700">
            <p className="text-xs text-slate-500 uppercase tracking-wider px-4 mb-2">Revenue</p>
            <Link to="/admin/advertisers" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <Briefcase className="w-5 h-5" /><span>Advertisers</span>
            </Link>
            <Link to="/admin/campaigns" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <BarChart3 className="w-5 h-5" /><span>Campaigns</span>
            </Link>
            <Link to="/admin/billing" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <DollarSign className="w-5 h-5" /><span>Billing</span>
            </Link>
            <Link to="/admin/revenue" className="flex items-center gap-3 px-4 py-3 rounded-lg bg-slate-800 text-white">
              <BarChart3 className="w-5 h-5" /><span>Revenue Dashboard</span>
            </Link>
          </div>

          {/* Signage CMS Section */}
          <div className="pt-4 mt-4 border-t border-slate-700">
            <p className="text-xs text-slate-500 uppercase tracking-wider px-4 mb-2">Signage CMS</p>
            <Link to="/admin/screens" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <Monitor className="w-5 h-5" /><span>Screens</span>
            </Link>
            <Link to="/admin/content" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <FileText className="w-5 h-5" /><span>Content</span>
            </Link>
            <Link to="/admin/playlists" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <LayoutDashboard className="w-5 h-5" /><span>Playlists</span>
            </Link>
          </div>
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <div className="flex items-center gap-3 mb-4">
            <ParkWiseLogo size="sm" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm w-full">
            <LogOut className="w-4 h-4" /><span>Sign out</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="ml-64 p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Revenue Dashboard</h1>
            <p className="text-slate-400">Track MRR, campaign performance, and financial metrics</p>
          </div>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-32 bg-slate-800 border-slate-700 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
              <SelectItem value="12m">Last 12 months</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Key Metrics */}
        {overview && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
            <Card 
              className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 border-emerald-500/30 cursor-pointer hover:border-emerald-400 transition-colors"
              onClick={() => navigate("/admin/billing")}
              data-testid="kpi-mrr"
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-emerald-300">MRR</CardTitle>
                <TrendingUp className="w-5 h-5 text-emerald-400" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-white">{formatCurrency(overview.mrr)}</div>
                <p className="text-xs text-emerald-300 mt-1">Monthly recurring</p>
              </CardContent>
            </Card>
            <Card 
              className="bg-slate-800/50 border-slate-700 cursor-pointer hover:border-blue-500/50 transition-colors"
              onClick={() => navigate("/admin/billing")}
              data-testid="kpi-total-revenue"
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-400">Total Revenue</CardTitle>
                <DollarSign className="w-5 h-5 text-blue-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-white">{formatCurrency(overview.total_revenue)}</div>
              </CardContent>
            </Card>
            <Card 
              className="bg-slate-800/50 border-slate-700 cursor-pointer hover:border-purple-500/50 transition-colors"
              onClick={() => navigate("/admin/campaigns")}
              data-testid="kpi-active-campaigns"
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-400">Active Campaigns</CardTitle>
                <Activity className="w-5 h-5 text-purple-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-white">{overview.active_campaigns}</div>
                <p className="text-xs text-slate-500">of {overview.total_campaigns} total</p>
              </CardContent>
            </Card>
            <Card 
              className="bg-slate-800/50 border-slate-700 cursor-pointer hover:border-amber-500/50 transition-colors"
              onClick={() => navigate("/admin/advertisers")}
              data-testid="kpi-advertisers"
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-400">Advertisers</CardTitle>
                <Briefcase className="w-5 h-5 text-amber-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-white">{overview.active_advertisers}</div>
              </CardContent>
            </Card>
            <Card 
              className="bg-slate-800/50 border-slate-700 cursor-pointer hover:border-red-500/50 transition-colors"
              onClick={() => navigate("/admin/billing?status=outstanding")}
              data-testid="kpi-outstanding"
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-400">Outstanding</CardTitle>
                <TrendingDown className="w-5 h-5 text-red-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-white">{formatCurrency(overview.outstanding)}</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* KPIs */}
        {kpis && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div 
              className="bg-slate-800/30 rounded-lg p-4 border border-slate-700 cursor-pointer hover:border-blue-500/50 transition-colors"
              onClick={() => navigate("/admin/advertisers")}
              data-testid="kpi-new-advertisers"
            >
              <p className="text-xs text-slate-500 uppercase">New Advertisers (30d)</p>
              <p className="text-xl font-bold text-white">{kpis.new_advertisers_30d}</p>
            </div>
            <div 
              className="bg-slate-800/30 rounded-lg p-4 border border-slate-700 cursor-pointer hover:border-purple-500/50 transition-colors"
              onClick={() => navigate("/admin/campaigns")}
              data-testid="kpi-avg-campaign"
            >
              <p className="text-xs text-slate-500 uppercase">Avg Campaign Value</p>
              <p className="text-xl font-bold text-white">{formatCurrency(kpis.avg_campaign_value)}</p>
            </div>
            <div 
              className="bg-slate-800/30 rounded-lg p-4 border border-slate-700 cursor-pointer hover:border-green-500/50 transition-colors"
              onClick={() => navigate("/admin/billing")}
              data-testid="kpi-collection-rate"
            >
              <p className="text-xs text-slate-500 uppercase">Collection Rate</p>
              <p className="text-xl font-bold text-white">{kpis.invoice_collection_rate}%</p>
            </div>
            <div 
              className="bg-slate-800/30 rounded-lg p-4 border border-slate-700 cursor-pointer hover:border-amber-500/50 transition-colors"
              onClick={() => navigate("/admin/screens")}
              data-testid="kpi-screen-utilization"
            >
              <p className="text-xs text-slate-500 uppercase">Screen Utilization</p>
              <p className="text-xl font-bold text-white">{kpis.screen_utilization}%</p>
            </div>
          </div>
        )}

        <Tabs defaultValue="sites" className="space-y-6">
          <TabsList className="bg-slate-800 border-slate-700">
            <TabsTrigger value="sites">By Site</TabsTrigger>
            <TabsTrigger value="advertisers">By Advertiser</TabsTrigger>
            <TabsTrigger value="campaigns">Campaign Performance</TabsTrigger>
            <TabsTrigger value="types">By Type</TabsTrigger>
          </TabsList>

          <TabsContent value="sites">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Revenue by Site</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {bySite.map((site) => (
                    <div key={site.site_id} className="flex items-center justify-between p-4 bg-slate-900/50 rounded-lg">
                      <div>
                        <p className="font-medium text-white">{site.site_name}</p>
                        <p className="text-sm text-slate-400">
                          {site.active_campaigns} campaigns • {site.screen_count} screens
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-white">{formatCurrency(site.total_revenue)}</p>
                        <p className="text-xs text-slate-400">
                          Ads: {formatCurrency(site.campaign_revenue)} • Boosts: {formatCurrency(site.boost_revenue)}
                        </p>
                      </div>
                    </div>
                  ))}
                  {bySite.length === 0 && (
                    <p className="text-center text-slate-400 py-8">No site data available</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="advertisers">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Revenue by Advertiser</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {byAdvertiser.slice(0, 10).map((adv) => (
                    <div key={adv.advertiser_id} className="flex items-center justify-between p-4 bg-slate-900/50 rounded-lg">
                      <div>
                        <p className="font-medium text-white">{adv.company_name}</p>
                        <p className="text-sm text-slate-400">
                          {adv.active_campaigns} active • {adv.campaign_count} total campaigns
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-white">{formatCurrency(adv.total_spend)}</p>
                        <p className="text-xs text-slate-400">
                          Outstanding: {formatCurrency(adv.outstanding)}
                        </p>
                      </div>
                    </div>
                  ))}
                  {byAdvertiser.length === 0 && (
                    <p className="text-center text-slate-400 py-8">No advertiser data available</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="campaigns">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Campaign Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-700">
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Campaign</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Advertiser</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-slate-400">Impressions</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-slate-400">Spend</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-slate-400">eCPM</th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-slate-400">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {campaigns.map((campaign) => (
                        <tr key={campaign.campaign_id} className="border-b border-slate-800">
                          <td className="py-3 px-4 text-white">{campaign.name}</td>
                          <td className="py-3 px-4 text-slate-400">{campaign.advertiser_name}</td>
                          <td className="py-3 px-4 text-right text-white">{campaign.impressions.toLocaleString()}</td>
                          <td className="py-3 px-4 text-right text-white">{formatCurrency(campaign.spend)}</td>
                          <td className="py-3 px-4 text-right text-white">{formatCurrency(campaign.cpm_effective)}</td>
                          <td className="py-3 px-4 text-center">
                            <span className={`px-2 py-1 rounded text-xs ${
                              campaign.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' :
                              campaign.status === 'paused' ? 'bg-orange-500/20 text-orange-400' :
                              'bg-slate-500/20 text-slate-400'
                            }`}>
                              {campaign.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {campaigns.length === 0 && (
                    <p className="text-center text-slate-400 py-8">No campaign data available</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="types">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Revenue by Campaign Type</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {Object.entries(byCampaignType).map(([type, data]) => (
                    <div key={type} className="p-4 bg-slate-900/50 rounded-lg">
                      <p className="text-sm text-slate-400 capitalize">{type.replace("_", " ")}</p>
                      <p className="text-2xl font-bold text-white">{formatCurrency(data.revenue)}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        {data.count} campaigns • {data.active} active
                      </p>
                    </div>
                  ))}
                  {Object.keys(byCampaignType).length === 0 && (
                    <p className="col-span-4 text-center text-slate-400 py-8">No data available</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
